import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import Loader from 'components/Loader/Loader';
import { push } from 'connected-react-router';
import Reports from 'components/Dashboard/Modules/Reports/Reports';
import ReportMenu from 'components/Dashboard/Modules/Reports/ReportMenu';
import WarningBox from 'components/WarningBox/WarningBox';
import useFilters from 'hooks/useFilters';
import { AppState } from 'reducers/reducers';
import { selectedSpotId, selectedSpotInfo } from 'selectors';
import { trackPageViewed } from 'utils/analytics';
import { RentalTypeFilterValue, TICKET_RENTAL_TYPE } from 'utils/constants';
import { isValidDateForReports, isValidRentalTypeForReports } from 'utils/helpers';
import MonthYearUtil, { MonthYear } from 'utils/monthYear';
import { useDailyReports } from 'contexts/DailyReportContext';
import { useOrganization } from 'contexts/OrganizationsContext';

type ReportsModuleContainerProps = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

function ReportsModuleContainer({
    selectedSpot,
    selectedSpotPk,
    profile,
    push: connectedPush,
}: ReportsModuleContainerProps) {
    const monthYearUtil = new MonthYearUtil();
    const { organization } = useOrganization();
    const { reports: dailyReports, loading, errorMessage, getDailyReports } = useDailyReports();

    const { filters, urlUpdater } = useFilters<{
        monthYear: MonthYear['value'];
        rentalType: RentalTypeFilterValue;
    }>({
        filterValidators: {
            monthYear(currentValue: string | null): string {
                return isValidDateForReports(currentValue) ? currentValue : monthYearUtil.currentMonthYear.value;
            },
            rentalType(currentValue: string | null): RentalTypeFilterValue {
                return isValidRentalTypeForReports(currentValue) ? currentValue : '';
            },
        },
        push: connectedPush,
    });

    useEffect(() => {
        getDailyReports(
            {
                rentalType: filters.rentalType,
                monthYear: filters.monthYear,
            },
            selectedSpotPk
        );
    }, [getDailyReports, filters.monthYear, filters.rentalType, selectedSpotPk]);

    // DailyReportsProvider is mounted app-wide (App.tsx) and its reducer keeps the previous
    // payload on both request and failure, so `dailyReports` can still belong to the
    // previously-viewed spot. Only trust the flag once a fresh, successful report has landed
    // — otherwise switching from a ticket-sharing spot to one that doesn't share would keep
    // offering "Violations", indefinitely if the new request fails.
    const hasFreshReport = !!dailyReports && !loading && !errorMessage;
    const hasTicketRevenue = hasFreshReport && !!dailyReports?.has_ticket_revenue;

    // Drop a "Violations" filter that arrived from a hand-edited or stale URL. The report is
    // already ticket-free for these spots; this stops the dropdown showing a dead selection.
    useEffect(() => {
        if (hasFreshReport && !hasTicketRevenue && filters.rentalType === TICKET_RENTAL_TYPE) {
            urlUpdater.rentalType('');
        }
    }, [hasFreshReport, hasTicketRevenue, filters.rentalType, urlUpdater]);

    useEffect(() => {
        trackPageViewed({
            pageName: 'Reports',
            organizationId: organization?.pk,
            spotId: selectedSpotPk,
            affiliationRole: organization?.role,
            superuser: profile.superuser,
        });
    }, [organization?.pk, organization?.role, selectedSpotPk, profile.superuser]);

    if (!selectedSpot) {
        return <Loader />;
    }

    function handleDateFilterSelected(value?: string): void {
        urlUpdater.monthYear(value ? value : monthYearUtil.currentMonthYear.value);
    }

    function handleRentalTypeFilterSelected(value: RentalTypeFilterValue): void {
        urlUpdater.rentalType(value);
    }

    return (
        <>
            {errorMessage && (
                <div style={{ marginBottom: '20px' }}>
                    <WarningBox type="error">{errorMessage}</WarningBox>
                </div>
            )}
            <ReportMenu
                spot={selectedSpot}
                handleDateFilterSelected={handleDateFilterSelected}
                handleRentalTypeFilterSelected={handleRentalTypeFilterSelected}
                filters={filters}
                hasTicketRevenue={hasTicketRevenue}
            />
            <Reports selectedDailyReports={dailyReports} filters={filters} loading={loading} />
        </>
    );
}

function mapStateToProps(state: AppState) {
    return {
        selectedSpot: selectedSpotInfo(state),
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        profile: state.auth.profile!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        selectedSpotPk: selectedSpotId(state)!,
    };
}

const mapDispatchToProps = {
    push: push,
};

export default connect(mapStateToProps, mapDispatchToProps)(ReportsModuleContainer);
